import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DebounceInput } from 'react-debounce-input';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-fetching-library';

import { AlertDialogContainer } from '../others/AlertDialogContainer';
import { setToken,
  setGroup,
  setResetPasswordRequired,
  setUserEmail,
  setOLOrderFilters,
  setZSOrderFilters,
  setPLOrderFilters,
  setWROrderFilters,
  setOLOrderHiddenColumns,
  setZSOrderHiddenColumns,
  setPLOrderHiddenColumns,
  setWROrderHiddenColumns,
  setOLOrderColumnsWidth,
  setZSOrderColumnsWidth,
  setPLOrderColumnsWidth,
  setWROrderColumnsWidth,
  setOLOrderColumnsSequence,
  setZSOrderColumnsSequence,
  setPLOrderColumnsSequence,
  setWROrderColumnsSequence,
  setOLOrderSearchFields,
  setZSOrderSearchFields,
  setPLOrderSearchFields,
  setWROrderSearchFields,
  setEOrderColumnsSequence,
  setEOrderColumnsWidth,
  setEAOrderColumnsWidth,
  setEAOrderColumnsSequence,
  setEmployeeId,
  setEmployeeConfigId,
  setEmployeePermissions,
  setResetPasswordLink } from '../../services/redux-actions';
import { getRecruitmentNewsBaner, loginAction, logoutAction } from '../../services/request';
import { useFullScreen } from '../../services/hooks';

export const LoginPage = ({ forStaff = false, admin = false }) => {
  const { mutate: logoutMutate } = useMutation(logoutAction);
  const recruitmentBanerQuery = useQuery(getRecruitmentNewsBaner());
  const mediaWidth = useFullScreen() ? '300px' : '450px';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, mutate, error } = useMutation(loginAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [preview, setPreview] = React.useState(null);

  const token = useSelector(state => state.app.token);

  const hasClientSession = Boolean(
    token || document.cookie.split('; ').some(cookie => cookie.startsWith('ss_token='))
  );

  const goToClientPortal = () => {
    window.location.href = `${process.env.REACT_APP_PORTAL_WWW_URL}/panel`;
  };

  const handleLogout = async () => {
    const { error } = await logoutMutate();
    if (!error) {
      dispatch(setToken(null));
    }
  };

  const validationSchema = Yup.object({
    email: Yup.string().required('To pole nie może być puste'),
    password: Yup.string()
      .required('To pole nie może być puste')
      .min(4, 'Hasło musi mieć co najmniej 4 znaki')
      .max(24, 'Hasło może mięc najwyżej 24 znaków')
  });

  const initialValues = {
    email: '',
    password: ''
  };

  const setModuleFilter = payload => {
    if (module === 'obozy') {
      return setOLOrderFilters(payload.user.config.ol_order_filters);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderFilters(payload.user.config.zs_order_filters);
    } else if (module === 'polkolonie') {
      return setPLOrderFilters(payload.user.config.pl_order_filters);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderFilters(payload.user.config.wr_order_filters);
    }

    return setOLOrderFilters(payload.user.config.ol_order_filters);
  };

  const setModuleHiddenColumns = payload => {
    if (module === 'obozy') {
      return setOLOrderHiddenColumns(payload.user.config.ol_order_hidden_columns);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderHiddenColumns(payload.user.config.zs_order_hidden_columns);
    } else if (module === 'polkolonie') {
      return setPLOrderHiddenColumns(payload.user.config.pl_order_hidden_columns);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderHiddenColumns(payload.user.config.wr_order_hidden_columns);
    }

    return setOLOrderHiddenColumns(payload.user.config.ol_order_hidden_columns);
  };

  const setModuleColumnsWidth = payload => {
    if (module === 'obozy') {
      return setOLOrderColumnsWidth(payload.user.config.ol_order_columns_width);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsWidth(payload.user.config.zs_order_columns_width);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsWidth(payload.user.config.pl_order_columns_width);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsWidth(payload.user.config.wr_order_columns_width);
    }


    return setOLOrderColumnsWidth(payload.user.config.ol_order_columns_width);
  };

  const setModuleColumnsSequence = payload => {
    if (module === 'obozy') {
      return setOLOrderColumnsSequence(payload.user.config.ol_order_columns_sequence);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderColumnsSequence(payload.user.config.zs_order_columns_sequence);
    } else if (module === 'polkolonie') {
      return setPLOrderColumnsSequence(payload.user.config.pl_order_columns_sequence);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderColumnsSequence(payload.user.config.wr_order_columns_sequence);
    }

    return setOLOrderColumnsSequence(payload.user.config.ol_order_columns_sequence);
  };

  const setModuleSearchFields = payload => {
    if (module === 'obozy') {
      return setOLOrderSearchFields(payload.user.config.ol_order_search_fields);
    } else if (module === 'zielone_szkoly') {
      return setZSOrderSearchFields(payload.user.config.zs_order_search_fields);
    } else if (module === 'polkolonie') {
      return setPLOrderSearchFields(payload.user.config.pl_order_search_fields);
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderSearchFields(payload.user.config.wr_order_search_fields);
    }

    return setOLOrderSearchFields(payload.user.config.ol_order_search_fields);
  };

  const handleSubmit = async values => {
    if (admin) {
      values.admin = true;
    }
    const { error, payload } = await mutate(values);
    if (!error) {
      console.log(payload);
      dispatch(setToken(payload.access));
      dispatch(setGroup(payload.user.groups[0]));
      dispatch(setUserEmail(payload.user.email));
      dispatch(setResetPasswordRequired(payload.user.password_reset_required));
      dispatch(setResetPasswordLink(payload.user.reset_password_link));
      if (payload.user.groups[0] === 'clients') {
        navigate('client');
      } else if (payload.user.groups[0] === 'staff' && payload.user.password_reset_required) {
        dispatch(setEmployeeId(payload.user.config.employee_id));
        navigate(payload.user.reset_password_link);
      } else if (payload.user.groups[0] === 'staff' && !payload.user.password_reset_required) {
        dispatch(setEmployeeId(payload.user.config.employee_id));
        navigate('staff');
      } else if (payload.user.groups[0] === 'managers' && payload.user.password_reset_required) {
        dispatch(setEmployeeId(payload.user.config.employee_id));
        navigate(payload.user.reset_password_link);
      } else if (payload.user.groups[0] === 'managers' && !payload.user.password_reset_required) {
        dispatch(setEmployeeId(payload.user.config.employee_id));
        navigate('staff');
      } else {
        if (payload.user.config) {
          dispatch(setEmployeeId(payload.user.config.employee_id));
          dispatch(setEmployeeConfigId(payload.user.config.employee_config_id));
          dispatch(setModuleFilter(payload));
          dispatch(setModuleHiddenColumns(payload));
          dispatch(setModuleColumnsWidth(payload));
          dispatch(setModuleColumnsSequence(payload));
          dispatch(setModuleSearchFields(payload));
          if (payload.user.config.e_columns_sequence) {
            dispatch(setEOrderColumnsSequence(payload.user.config.e_columns_sequence));
          }
          dispatch(setEOrderColumnsWidth(payload.user.config.e_columns_width));
          if (payload.user.config.ea_columns_sequence) {
            dispatch(setEAOrderColumnsSequence(payload.user.config.ea_columns_sequence));
          }
          dispatch(setEAOrderColumnsWidth(payload.user.config.ea_columns_width));
          dispatch(setEmployeePermissions(payload.user.config.employee_permissions));
        }
        navigate('/employee');
      }
    } else {
      setSeverity('error');
      setTitle('Podano email i/lub hasło są nieprawidłowe');
      setMessage('Spróbuj ponownie jeszcze raz');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  React.useEffect(() => {
    if (!recruitmentBanerQuery.error && !recruitmentBanerQuery.loading) {
      setPreview(recruitmentBanerQuery.payload.recruitment_news_baner);
    }
  }, [recruitmentBanerQuery.loading]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      component={Paper}
      elevation={4}
      padding={8}
    >
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Box>
        {forStaff && <img
          src={preview}
          style={{ width: '100%', height: '150px' }}
        >
        </img>}
        <Typography color="primary" variant="h5" align="center">
          {forStaff ? 'Logowanie do panelu pracownika' : 'Logowanie do panelu klienta'}
        </Typography>
      </Box>
      <Divider style={{ width: mediaWidth, marginTop: 20 }} />
      {hasClientSession
        ? <Box
          sx={{
            width: mediaWidth,
            mt: 5,
            display: 'flex',
            gap: 2
          }}
        >
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={goToClientPortal}
          >
          Przejdź do panelu klienta
          </Button>

          <Button
            color="secondary"
            variant="contained"
            onClick={handleLogout}
          >
          Wyloguj
          </Button>
        </Box>
        : <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <FormControl
            style={{
              marginTop: 50,
              marginBottom: 20,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Adres e-mail
            </Typography>
            <DebounceInput
              variant="outlined"
              name="email"
              id="email"
              type="text"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <FormControl
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Hasło
            </Typography>
            <DebounceInput
              variant="outlined"
              type="password"
              name="password"
              id="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenIcon />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <Divider style={{ width: mediaWidth, marginTop: 50, marginBottom: 20 }} />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            style={{ marginBottom: 20 }}
            disabled={loading}
          >
          Zaloguj
          </Button>
        </form>}

      {!hasClientSession && <Box display="flex" flexDirection="row" justifyContent="center">
        {forStaff == false
          ? <Button onClick={() => navigate('/registration')}>
            <Typography variant="subtitle2">Nie masz konta? <u>Zarejestruj się</u></Typography>
          </Button>
          : null}
        <Divider orientation="vertical" />
        <Button onClick={() => navigate('/restore-password')}>
          <Typography variant="subtitle2">Przypomnij hasło</Typography>
        </Button>
      </Box>}
      <Box height={30}/>
      {forStaff ? <Alert severity="info" sx={{ maxWidth: mediaWidth }}>
        <p>
          Korzystanie przez Ciebie z panelu Kartoteka wiąże się z przetwarzaniem przez nas -
          Organizatora Aktywnego Wypoczynku SUN SPORT Jolanta Walas - Zdunek, Twoich danych osobowych.
          Kliknij <a href="https://sun-sport.pl/polityka-prywatnosci-sunsport/"><b>TUTAJ</b></a>,
          aby zapoznać się z naszą Polityką prywatności,
         w tym ze szczegółowymi informacjami dotyczącymi wykorzystania przez nas tych danych.
        </p>
      </Alert> : null}
    </Box>
  );
};
